import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { ItemVerification } from "./ItemVerification";
import {
  useNextPendingTask,
  useFetchTask,
  useImageGalleryQuery,
  useFetchJob,
  useBenchmarkQuizMetrics,
} from "@labeling-tools/api/lebron/react-query-hooks";
import { QuizMetricCard } from "./QuizMetricCard";
import { Button, LoadingOverlay } from "@mantine/core";
import { JobStatus, JobType } from "@augmodo/lebron-browser-client";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useLocation } from "react-router-dom";
import { PLAY_MODE_JOB_TYPE } from "@/config/data";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import {
  reset,
  setIsPlayMode,
  setPlaylistIndex,
  clearTaskPlaylist,
  setGroupedEventsIndex,
} from "../store";
import { StickyActionsBar } from "./StickyActionsBar";
import { useFetchParcel } from "@labeling-tools/api/lebron/react-query-hooks";
import {
  getNextPendingTaskData,
  getSavedGroupedEventsIndex,
  resolveGalleryImageUrl,
} from "../utils/helpers";
import { ViewMode } from "@/types";
import { useEvents } from "@/hooks/useEvents";
import { useNextTaskPrefetch } from "@/hooks/useNextTaskPrefetch";
import { useFetchNextPlayModeJob } from "@/hooks/useFetchNextPlayModeJob";
import { resolveTaskPlanogramId } from "@/hooks/useTaskPlanogram";
import { useBounceToJobs } from "@/hooks/useBounceToJobs";
import { useProfileHome } from "@/hooks/useProfileHome";
import { useGate } from "statsig-react";
import {
  IdleTimeProvider,
  IdleWarningModal,
  ExitModal,
} from "@labeling-tools/ui-components/src";
import { TimeTrackingProvider } from "@/contexts/TimeTrackingContext";

export const OosConfirmationContent = ({ mode }: { mode?: ViewMode }) => {
  const navigate = useNavigate();
  const { jobsPath } = useProfileHome();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const taskId = searchParams.get("taskId");
  const { jobId } = useParams<{ jobId: string }>();
  const queryClient = useQueryClient();
  const groupedEventsIndex = useAppSelector(
    (state) => state.oos.groupedEventsIndex,
  );
  const taskPlaylist = useAppSelector((state) => state.oos.taskPlaylist);
  const playlistIndex = useAppSelector((state) => state.oos.playlistIndex);
  const playlistActive = taskPlaylist.length > 0;

  // How we fetch the task: admin / qa-feedback always fetch a specific task by
  // id. In "My Tasks" play mode (viewer + active playlist) we also fetch the
  // specific assigned task by id, so the user only ever works their own tasks —
  // never the job's other pending tasks that next-pending-task would serve.
  // Both hooks are always called (rules of hooks); `enabled` toggles which runs.
  const fetchById =
    mode === "admin" ||
    mode === "confirmation-qa-feedback" ||
    (mode === "viewer" && playlistActive);
  const fetchTaskQuery = useFetchTask(jobId, taskId, fetchById);
  const nextPendingQuery = useNextPendingTask(jobId, !fetchById);
  const {
    data: nextPendingTask,
    isLoading: nextPendingTaskLoading,
    isError: nextPendingTaskError,
  } = fetchById ? fetchTaskQuery : nextPendingQuery;

  // Whether this is a quiz has to come from the *job*, not the task: once the
  // last question is answered there is no next pending task, so the task-level
  // `isBenchmarkTask` below is false exactly when a quiz has just been finished.
  const { data: job } = useFetchJob(jobId ?? "");
  const isBenchmarkJob =
    job?.type === "OUT_OF_STOCK_CONFIRMATION_BENCHMARK_TASKS";

  const { events } = useEvents(
    nextPendingTask?.data?.grouped_oos_events as string[],
  );
  const event =
    groupedEventsIndex !== null
      ? events[groupedEventsIndex]?.payload
      : nextPendingTask?.data;
  const { data: parcel, isLoading: parcelLoading } = useFetchParcel(
    event?.parcel_id as string | undefined,
  );
  const nextPendingTaskData = getNextPendingTaskData(
    parcel,
    event,
    nextPendingTask,
  );

  // For play mode's "fetch next job" call, once this task finishes -- read
  // directly off the raw task/parcel (not nextPendingTaskData, which is
  // shaped for gallery rendering) so it matches what useTaskPlanogram itself
  // resolves.
  const currentPlanogramId = resolveTaskPlanogramId(nextPendingTask, parcel);

  const mainImageIdForGallery = nextPendingTaskData?.image_id
    ? [nextPendingTaskData.image_id as string]
    : undefined;
  const refImageId =
    nextPendingTaskData?.data?.reference_images?.[
      nextPendingTaskData?.data?.reference_image_index
    ]?.image_id;
  const referenceImageIdForGallery = refImageId
    ? [refImageId as string]
    : undefined;

  // fetch image gallery - main image
  const { data: mainImageGallery, isLoading: mainImageLoading } =
    useImageGalleryQuery(mainImageIdForGallery);

  // fetch image gallery - reference image
  const { data: referenceImageGallery, isLoading: referenceImageLoading } =
    useImageGalleryQuery(referenceImageIdForGallery);

  // Prefetch the next pending task as soon as the task mounts.
  // Applies to auto-advancing modes (viewer + confirmation-qa); admin /
  // qa-feedback navigate to a fixed task id, so prefetch doesn't apply.
  const prefetchEnabled =
    (mode === undefined || mode === "viewer" || mode === "confirmation-qa") &&
    !playlistActive;
  const { nextTaskId, isPrefetchReady } = useNextTaskPrefetch({
    jobId,
    currentTaskId: nextPendingTask?.id ?? undefined,
    enabled: prefetchEnabled,
  });

  // Which grouped image set (see QuantitySlider) the task's result was
  // submitted against, if any — restores the admin/QA view to what the
  // original labeler actually saw instead of always defaulting to set 0.
  const savedGroupedEventsIndex = useMemo(
    () => getSavedGroupedEventsIndex(nextPendingTask),
    [nextPendingTask],
  );

  // any time we start loading a new task, reset the store and seed the
  // grouped-events index from the task's saved result (null for a fresh,
  // not-yet-completed task, which matches the prior default).
  useEffect(() => {
    dispatch(reset());
    dispatch(setGroupedEventsIndex(savedGroupedEventsIndex));
  }, [nextPendingTask?.id, savedGroupedEventsIndex, dispatch]);

  // if nextPendingTask errors (usually all tasks for a job are completed) return null
  useEffect(() => {
    if (nextPendingTaskError) {
      queryClient.setQueryData(["nextPendingTask", jobId], null);
    }
  }, [nextPendingTaskError, queryClient, jobId]);

  // Pass the GCS signed URL through to leaf components (ImageWithBoundingBox).
  const missingItemImageUrl = resolveGalleryImageUrl(
    mainImageGallery?.images[0],
  );
  const foundItemImageUrl = resolveGalleryImageUrl(
    referenceImageGallery?.images[0],
  );

  const { value: benchmarkEnabled, isLoading: benchmarkGateLoading } = useGate(
    "fe_enable_oos_benchmark_quiz",
  );
  const isBenchmarkTask =
    (nextPendingTask as any)?.type === "OUT_OF_STOCK_CONFIRMATION_BENCHMARK";

  const planogramOnlyView =
    !!nextPendingTaskData?.data?.identified_product_id ||
    !!(nextPendingTask?.data as any)?.box_draw_task_id;

  const noGallery =
    !mainImageLoading && !referenceImageLoading && !mainImageGallery && !parcel;
  const noImagesFound =
    !nextPendingTaskData ||
    (!planogramOnlyView &&
      (!mainImageGallery ||
        !referenceImageGallery ||
        !missingItemImageUrl ||
        !foundItemImageUrl));

  // Note: `nextPendingTaskFetching` is intentionally excluded — after a
  // submit, the prefetched task is written into the cache and we kick off a
  // background reconciliation refetch. We don't want LoadingOverlay to flash
  // during that reconciliation, since the cached prefetched task is already
  // a valid render.
  const isLoadingAny =
    mainImageLoading ||
    referenceImageLoading ||
    nextPendingTaskLoading ||
    parcelLoading;

  const isEmptyState = !isLoadingAny && (noGallery || noImagesFound);

  // Scored results for a quiz the reviewer just finished. Gated with the rest of
  // the benchmark feature so a treatment-group-only quiz can't leak a card.
  //
  // Keyed off `isEmptyState`, not the raw noGallery/noImagesFound flags: those
  // are also briefly true *between* questions while the next task loads. Firing
  // then caches a mid-quiz snapshot (status still NEEDS_REVIEW, partial counts)
  // that gets replayed after the final answer — the reviewer lands on "No task
  // found", and the My Quizzes modal reads the same cache key and shows the same
  // empty stats until a hard refresh.
  const { data: quizMetrics, isLoading: quizMetricsLoading } =
    useBenchmarkQuizMetrics(
      isBenchmarkJob && benchmarkEnabled && isEmptyState
        ? (jobId ?? null)
        : null,
    );

  const playMode = useAppSelector((state) => state.oos.isPlayMode);
  const location = useLocation();
  const routeKey = location.pathname.split("/").pop() ?? "";
  const playModeConfig = PLAY_MODE_JOB_TYPE[routeKey];
  const [noNextJobAvailable, setNoNextJobAvailable] = useState(false);

  // "My Tasks" play mode safety net: if a queued task renders with no images,
  // auto-skip forward to the next assigned task. The normal advance happens on
  // submit (StickyActionsBar). Each entry targets a specific task via ?taskId=.
  useEffect(() => {
    if (!isEmptyState || !playMode || noNextJobAvailable || !playlistActive)
      return;

    const nextIndex = playlistIndex + 1;
    if (nextIndex < taskPlaylist.length) {
      dispatch(setPlaylistIndex(nextIndex));
      const next = taskPlaylist[nextIndex];
      navigate(`/jobs/${next.jobId}/${next.route}?taskId=${next.taskId}`);
    } else {
      dispatch(clearTaskPlaylist());
      dispatch(setIsPlayMode(false));
      setNoNextJobAvailable(true);
    }
  }, [
    isEmptyState,
    playMode,
    noNextJobAvailable,
    playlistActive,
    playlistIndex,
    taskPlaylist,
    navigate,
    dispatch,
  ]);

  const onNoNextJobFound = useCallback(() => {
    setNoNextJobAvailable(true);
    dispatch(setIsPlayMode(false));
  }, [dispatch]);

  // Whether this visit was served a task at all — if so, running out means
  // the labeler finished the job rather than finding it already taken.
  const servedTaskRef = useRef(false);
  if (nextPendingTask) servedTaskRef.current = true;

  // A claimable job that turned out to have no task for this labeler goes
  // back to its jobs tab with a notice, like Coverage Beta. Keyed on the
  // claim error rather than isEmptyState, which images failing to load also
  // trips. Fetch-by-id modes (admin, QA feedback, My Tasks) and quizzes keep
  // their own empty/result states; play mode bounces only once it has run
  // out of other jobs to move on to.
  const bounceToJobs = useBounceToJobs();
  const bounceTab =
    mode === "confirmation-qa" ? "confirmation-qa" : "confirmation";
  useEffect(() => {
    if (!nextPendingTaskError || fetchById || isBenchmarkJob) return;
    if (playMode && !noNextJobAvailable) return;
    // The error handler above caches `null` so a stale task doesn't render;
    // drop the entry so reopening the job asks the server again.
    queryClient.removeQueries({ queryKey: ["nextPendingTask", jobId] });
    bounceToJobs(
      bounceTab,
      jobId ?? "",
      servedTaskRef.current ? "finished" : "unavailable",
    );
  }, [
    nextPendingTaskError,
    fetchById,
    isBenchmarkJob,
    playMode,
    noNextJobAvailable,
    queryClient,
    jobId,
    bounceToJobs,
    bounceTab,
  ]);

  useFetchNextPlayModeJob({
    enabled:
      isEmptyState &&
      playMode &&
      !noNextJobAvailable &&
      !!playModeConfig &&
      !playlistActive,
    jobId,
    jobType: playModeConfig?.jobType as JobType,
    route: playModeConfig?.route ?? "",
    livePlanogramId: currentPlanogramId,
    onNoJobFound: onNoNextJobFound,
  });

  if (isLoadingAny || (isBenchmarkTask && benchmarkGateLoading)) {
    return (
      <div className="flex justify-center items-center">
        <LoadingOverlay visible={true} />
      </div>
    );
  }

  // Benchmark quiz tasks are served only to the treatment group; everyone
  // else gets the same empty state as an exhausted job.
  if (isBenchmarkTask && !benchmarkEnabled) {
    return (
      <div className="flex flex-col justify-center items-center pt-10">
        <div className="mb-4 text-xl">No task found</div>
        <div>
          <Button
            onClick={() => {
              navigate(jobsPath);
            }}
          >
            Go to dashboard
          </Button>
        </div>
      </div>
    );
  }

  if (noGallery || noImagesFound) {
    // A finished quiz lands here — there's no next pending task to serve. Show
    // the reviewer how they did instead of the generic empty state. Checked
    // before the play-mode branch so a quiz never gets auto-advanced past its
    // own results.
    //
    // The empty-gallery state has more causes than an exhausted quiz (images
    // failing to load is one), so lebron's job status is what decides whether
    // the quiz is actually over. Anything else falls through to "No task found"
    // rather than claiming "Quiz complete" over a quiz still holding questions.
    if (
      isBenchmarkJob &&
      benchmarkEnabled &&
      (quizMetricsLoading || quizMetrics)
    ) {
      if (quizMetrics?.status === JobStatus.COMPLETED) {
        return (
          <div className="mx-auto max-w-3xl px-4 pt-10">
            <QuizMetricCard
              title="Quiz complete"
              status={quizMetrics.status as JobStatus}
              subtitle={`${quizMetrics.store_name} · ${new Date(
                quizMetrics.created_at,
              ).toLocaleString()}`}
              precision={quizMetrics.precision}
              correct={quizMetrics.correct}
              answered={quizMetrics.answered}
              avgSecondsPerTask={quizMetrics.avg_seconds_per_task}
              tasksPerHour={quizMetrics.tasks_per_hour}
              onBack={() => navigate(`${jobsPath}?tab=my-quizzes`)}
            />
          </div>
        );
      }
      if (quizMetricsLoading) {
        return (
          <div className="flex justify-center items-center h-40 relative">
            <LoadingOverlay visible={true} />
          </div>
        );
      }
    }

    if (playMode && !noNextJobAvailable) {
      return (
        <div className="flex justify-center items-center h-40 relative">
          <LoadingOverlay visible={true} />
        </div>
      );
    }
    return (
      <div className="flex flex-col justify-center items-center pt-10">
        <div className="mb-4 text-xl">No task found</div>
        <div>
          <Button
            onClick={() => {
              navigate(jobsPath);
            }}
          >
            Go to dashboard
          </Button>
        </div>
      </div>
    );
  }
  const pendingTaskId = nextPendingTaskData?.id ?? "";

  const tabs = (
    <>
      <ItemVerification
        missingItemImageUrl={missingItemImageUrl}
        foundItemImageUrl={foundItemImageUrl}
        task={nextPendingTaskData as any}
        referenceImageCount={
          (nextPendingTask?.data as any)?.grouped_oos_events?.length
        }
        parcel={parcel!}
        mode={mode}
        // events={events}
      />
      <StickyActionsBar
        task={nextPendingTaskData as any}
        mode={mode}
        nextTaskId={nextTaskId}
        isPrefetchReady={isPrefetchReady}
      />
    </>
  );

  if (mode === "viewer" || mode === "confirmation-qa") {
    return (
      <IdleTimeProvider taskId={pendingTaskId}>
        {/* Time tracking only applies to labelers (viewer mode); QA/admin
            interactions fall back to the no-op context. */}
        {mode === "viewer" ? (
          <TimeTrackingProvider taskId={pendingTaskId}>
            {tabs}
          </TimeTrackingProvider>
        ) : (
          tabs
        )}
        <IdleWarningModal jobId={jobId || ""} taskId={pendingTaskId} />
        <ExitModal />
      </IdleTimeProvider>
    );
  }

  // admin / qa-feedback: no timing or idle tracking needed
  return tabs;
};
